/// <reference types="react-scripts" />
/* global JSX */
import Loader from "./../../../../Content/devel/images/large_loader.gif";
import * as React from "react";
import downloadjs from "downloadjs";
import * as taskService from "../../models/services/taskService";
import * as attachmentService from "../../models/services/attachmentService";
import { Translations } from "ui/Scripts/source/models/translations";
import { ToolButton } from "ui/Scripts/source/components/framework/toolButton";
import { Base } from "ui/Scripts/source/framework/base";
import { ListLine } from "../framework/listLine";
import { Dialog } from "../framework/dialog";
import { ITaskTypeCol, TaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { ITaskDataRow, TaskDataRow, SaveTaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskEditTypeItem, TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskDataRowCol, TaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { DialogButtonType, ConfirmationDialogResult, TaskDataRowDataType, TaskLockingType } from "ui/Scripts/source/models/enums";
import { TaskDynamicForm } from "./taskDynamicForm";
import { AttachmentList } from "../attachment/attachmentList";
import { SaveDataObj } from "../../framework/saveData";
import { IUnlockResult } from "../../models/common/unlockResult";
import { infiniteListPageHalfSize } from "ui/Scripts/source/models/consts";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { SaveAttachmentBase64 } from "../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/common/saveAttachmentBase64";
import { AppUtils } from "../../framework/appUtils";

//TaskDataRowDialog
//***************************************************************
export interface ITaskDataRowDialogProp {
    classes?: string;
    isReadOnly: boolean;
    userIsIsSystemAdmin: boolean;
    userCode: string;
    cameraIds: string[];
    adding: boolean;
    taskType: ITaskEditTypeItem;
    item: ITaskDataRow;
    photoAttachmentIds: string[];
    onAddPhoto: () => void;
    newCameraFileAttachment: FileAttachment;
    unlockTaskDataRow: (id: string, lockToCurrentUser: boolean, afterUnlock: (result: IUnlockResult) => void) => void;
    onOk: (cols: ITaskDataRowCol[], attachments: IFileAttachment[], photoAttachmentIds: string[], removedAttachmentIds: string[]) => void;
    onRemove: () => void;
    onCancel: () => void;
}

export interface ITaskDataRowDialogState {
    acceptClicks: boolean;
    cameraIds: string[];
    cols: ITaskDataRowCol[];
    attachments: IFileAttachment[];
    photoAttachmentIds: string[];
    removedAttachmentIds: string[];
    isLockedToOtherUser: boolean;
    lockedUserPicture: string;
    lockedUserName: string;
    enableDialogButtons: boolean;
}

export class TaskDataRowDialog extends React.Component<ITaskDataRowDialogProp, ITaskDataRowDialogState> {
    constructor(props) {
        super(props);
        const item = props.item;
        this.state = {
            cols: item.cols.map(i => new TaskDataRowCol(i)),
            attachments: item.attachments.map(i => new FileAttachment(i)),
            photoAttachmentIds: props.photoAttachmentIds.slice(0),
            removedAttachmentIds: [],
            isLockedToOtherUser: item.isLockedToOtherUser(props.userCode),
            lockedUserPicture: item.lockedUserPicture,
            lockedUserName: item.lockedUserName,
            acceptClicks: false,
            cameraIds: props.cameraIds.slice(0),
            enableDialogButtons: true
        };
        window.setTimeout(() => {
            this.setState({ acceptClicks: true });
        }, 250);
    }

    handleAddCameraFileAttachment = (attachment: FileAttachment) => {
        const state = this.state;
        const attachments = state.attachments.slice(0);
        const photoAttachmentIds = state.photoAttachmentIds.slice(0);
        attachments.push(attachment);
        photoAttachmentIds.push(attachment.id);
        this.setState({ attachments, photoAttachmentIds });
    };

    componentDidUpdate(prevProps: ITaskDataRowDialogProp) {
        const props = this.props;
        if (Base.isNullOrUndefined(props.newCameraFileAttachment)) return;
        if (Base.isNullOrUndefined(prevProps.newCameraFileAttachment) || props.newCameraFileAttachment.id !== prevProps.newCameraFileAttachment.id) {
            this.handleAddCameraFileAttachment(new FileAttachment(props.newCameraFileAttachment));
        }
    };

    handleRefreshTaskDataRowsForm = (taskDataRowCols: ITaskDataRowCol[]) => {
        this.setState({ cols: taskDataRowCols });
    };

    handleAttachmentDownload = async (id: string) => {
        if (!id) return;
        const attachment = this.state.attachments.find(i => i.id === id);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const result = await AppUtils.callService(() => attachmentService.getAttachment(id), false);
        if (!result) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, result));
    };

    handleAttachmentRemove = (id: string) => {
        if (Base.isNullOrEmpty(id)) return;
        const state = this.state;
        const attachments = state.attachments.filter(i => i.id !== id);
        const photoAttachmentIds = state.photoAttachmentIds.filter(i => i !== id);
        const removedAttachmentIds = state.removedAttachmentIds.slice(0);
        if (!this.props.adding) {
            removedAttachmentIds.push(id);
        }
        this.setState({ attachments, photoAttachmentIds, removedAttachmentIds });
    };

    handleAttachmentAdd = (fileList: FileList) => {
        FileAttachment.addFileListToFileAttachments(this.state.attachments, fileList, 0).then(attachments => {
            this.setState({ attachments });
        });
    };

    hasCameras = (): boolean => {
        return !Base.isNullOrUndefined(this.props.cameraIds) && this.props.cameraIds.length > 0;
    };

    handleAttachmentAddPhoto = () => {
        if (!this.hasCameras()) return;
        this.props.onAddPhoto();
    };

    handleUnlockClick = async () => {
        const props = this.props;
        const result = await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToUnlockTaskDataRow));
        if (result !== ConfirmationDialogResult.Yes) return;
        props.unlockTaskDataRow(props.item.id, true, (result: IUnlockResult) => {
            this.setState({
                isLockedToOtherUser: false,
                lockedUserName: "",
                lockedUserPicture: ""
            });
        });
    };

    handleEnableDialogButtons = (enableDialogButtons: boolean) => {
        this.setState({ enableDialogButtons });
    };

    handleOkClick = () => {
        const state = this.state;
        if (!state.acceptClicks) return;
        this.props.onOk(state.cols, state.attachments, state.photoAttachmentIds, state.removedAttachmentIds);
    };

    handleRemoveClick = () => {
        const state = this.state;
        if (!state.acceptClicks) return;
        this.props.onRemove();
    };

    handleCancelClick = () => {
        const state = this.state;
        if (!state.acceptClicks) return;
        this.props.onCancel();
    };

    render() {
        const props = this.props;
        const state = this.state;
        const dialogClasses = "taskDataRowDialog" + (!Base.isNullOrEmpty(props.classes) ? " " + props.classes : "");
        const formIsLocked = !props.adding && props.taskType.taskLockingType === TaskLockingType.LockSavedForm;
        return (
            <Dialog
                classes={dialogClasses}
                title={Translations.RowInformation}
                body={<div>
                    {state.isLockedToOtherUser &&
                        <div className="row lockedRow">
                            <div className="col-12">
                                <div className="fullLeft form-group">
                                    <div>
                                        <label className="control-label">{Translations.LockedToUser}</label>
                                        {props.userIsIsSystemAdmin &&
                                            <ToolButton
                                                tooltip={Translations.Unlock}
                                                classes={"unlock round mono right"}
                                                onClick={this.handleUnlockClick}
                                            />
                                        }
                                    </div>
                                    <div>
                                        <div className="iconCont">
                                            {!Base.isNullOrEmpty(state.lockedUserPicture) &&
                                                <img alt="..." src={state.lockedUserPicture} />
                                            }
                                        </div>
                                        <div className="dataCont withToolButton">
                                            <div className="singleLine">{state.lockedUserName}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    <div className="row">
                        <TaskDynamicForm
                            isReadOnly={props.isReadOnly || formIsLocked}
                            isEdit={!props.adding}
                            taskTypePages={props.taskType.pages}
                            taskTypeCols={props.taskType.formCols}
                            taskDataRowCols={state.cols}
                            attachmentsElement={
                                <AttachmentList
                                    classes="fullLeft"
                                    isReadOnly={props.isReadOnly || formIsLocked}
                                    canShowCamera={this.hasCameras()}
                                    lateThreshold={null}
                                    attachments={state.attachments}
                                    onDownloadAttachment={this.handleAttachmentDownload }
                                    onRemoveAttachment={this.handleAttachmentRemove}
                                    onAddAttachments={this.handleAttachmentAdd}
                                    onAddPhoto={this.handleAttachmentAddPhoto}
                                />
                            }
                            onRefreshCols={this.handleRefreshTaskDataRowsForm}
                            onEnableDialogButtons={this.handleEnableDialogButtons}
                        />
                    </div>
                    {!state.acceptClicks &&
                        <div className="clickShield"></div>
                    }
                </div>}
                buttons={[
                    { title: Translations.Save, buttonType: DialogButtonType.Primary, classes: (props.isReadOnly || formIsLocked ? " d-none" : ""), disabled: props.isReadOnly || formIsLocked || !state.enableDialogButtons, onClick: this.handleOkClick },
                    { title: Translations.Remove, buttonType: DialogButtonType.Default, classes: (props.isReadOnly ? " d-none" : ""), disabled: props.adding || props.isReadOnly || !state.enableDialogButtons, onClick: this.handleRemoveClick },
                    { title: !props.isReadOnly ? Translations.Cancel : Translations.Close, buttonType: DialogButtonType.Default, onClick: this.handleCancelClick }
                ]}
                onClose={this.handleCancelClick}
            />
        );
    }
}

//TaskDataRowListMenu
export interface ITaskDataRowListMenuProp {
    onRemove: () => void;
    onCancel: () => void;
}

export class TaskDataRowListMenu extends React.Component<ITaskDataRowListMenuProp, {}> {
    handleRemoveClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.onRemove();
    };

    handleCancelClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.onCancel();
    };
        
    render() {
        return (
            <div className="listPopupMenu" onClick={this.handleCancelClick}>
                <div className="menu">
                    <div className="line" onClick={this.handleRemoveClick}>{Translations.Remove}</div>
                    <div className="line" onClick={this.handleCancelClick}>{Translations.Cancel}</div>
                </div>
            </div>
        );
    }
}

//TaskDataRowListTitleRow
//***************************************************************
export interface ITaskDataRowListTitleRowProp {
    taskTypeCols: ITaskTypeCol[];
}

export class TaskDataRowListTitleRow extends React.Component<ITaskDataRowListTitleRowProp, {}> {
    render() {
        const taskTypeCols = this.props.taskTypeCols;
        return (
            <div className="fullLeft lineContainer">
                <div className="line title">
                    <div className="row">
                        {taskTypeCols.map((taskTypeCol) =>
                            <div key={taskTypeCol.field} className={!Base.isNullOrEmpty(taskTypeCol.class) ? taskTypeCol.class : ""}>{taskTypeCol.title}</div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

//TaskDataRowListLineCol
//***************************************************************
export interface ITaskDataRowListLineColProp {
    field: string;
    taskType: ITaskEditTypeItem;
    dataRow: ITaskDataRow;
    lineTaskTypeCol: ITaskTypeCol;
    modifyUserName: string;
}

export class TaskDataRowListLineCol extends React.Component<ITaskDataRowListLineColProp, {}> {
    render() {
        const props = this.props;
        const uiValue = props.dataRow.getTaskDataRowColListUiValue(props.field, props.taskType);
        const text = uiValue.text;
        let value: JSX.Element = null;
        if (props.lineTaskTypeCol.dataType === TaskDataRowDataType.Signature && text) {
            value = <img
                style={{
                    width: "100%",
                    objectFit: "contain"
                }}
                src={`data:image/png;base64,${text}`}
            />;
        }
        return (<div className={"c " + (!Base.isNullOrEmpty(props.lineTaskTypeCol.class) ? props.lineTaskTypeCol.class : "")}><div className="value">{value ?? text}</div></div>);
    }
}

//TaskDataRowListLine
//***************************************************************
export interface ITaskDataRowListLineProp {
    isReadOnly: boolean;
    userCode: string;
    taskType: ITaskEditTypeItem;
    lineTaskTypeCols: ITaskTypeCol[];
    item: ITaskDataRow;
    selectedId: string;
    isTotalLine?: boolean;
    onClick: (id: string) => void;
    onLongPress: (id: string) => void;
}

export class TaskDataRowListLine extends React.Component<ITaskDataRowListLineProp, {}> {
    private rowDiv: HTMLDivElement;
    private hasAttachment = false;
    private isLockedToOtherUser = false;

    setAttachmentMarkToLastVisibleCol = (hasAttachments: boolean, isLockedToOtherUser: boolean) => {
        const oldHasAttachment = this.hasAttachment;
        const oldIsLockedToOtherUser = this.isLockedToOtherUser;
        this.hasAttachment = hasAttachments;
        this.isLockedToOtherUser = isLockedToOtherUser;
        if (!this.rowDiv) return;
        const divs = this.rowDiv.querySelectorAll(".c");
        let lastCol: HTMLDivElement = null;
        for (let i = 0; i < divs.length; i++) {
            if ((divs[i] as HTMLDivElement).offsetParent === null) continue; //Hidden
            lastCol = divs[i] as HTMLDivElement;
        }
        if (!lastCol) return;
        const valueDiv = lastCol.querySelector(".value");
        if (!valueDiv) return;
        if (!hasAttachments && !isLockedToOtherUser) {
            valueDiv.className = "value";
            lastCol.classList.remove("flexCont");
            const iconDiv = lastCol.querySelectorAll(".iconCont");
            if (!iconDiv || !iconDiv.length) return;
            for (let i = 0; i < iconDiv.length; i++) {
                iconDiv[i].remove();
            }
        } else {
            lastCol.classList.add("flexCont");
            let iconCount = 0;
            if (hasAttachments) {
                iconCount = iconCount + 1;
                if (!oldHasAttachment) {
                    const newDiv = document.createElement("div");
                    newDiv.className = "iconCont a small flexRight";
                    const newSpan = document.createElement("span");
                    newSpan.className = "icon attachment";
                    newDiv.appendChild(newSpan);
                    lastCol.appendChild(newDiv);
                }
            } else if (oldHasAttachment && !hasAttachments) {
                const iconDiv = lastCol.querySelector(".iconCont.a");
                if (iconDiv) {
                    iconDiv.remove();
                }
            }
            if (isLockedToOtherUser) {
                iconCount = iconCount + 1;
                if (!oldIsLockedToOtherUser) {
                    const newDiv2 = document.createElement("div");
                    newDiv2.className = "iconCont l small flexRight";
                    const newSpan2 = document.createElement("span");
                    newSpan2.className = "icon lock gray";
                    newDiv2.appendChild(newSpan2);
                    lastCol.appendChild(newDiv2);
                }
            } else if (oldIsLockedToOtherUser && !isLockedToOtherUser) {
                const iconDiv = lastCol.querySelector(".iconCont.l");
                if (iconDiv) {
                    iconDiv.remove();
                }
            }
            valueDiv.className = "value" + (iconCount > 0 ? " withIcons" + iconCount.toString(10) : "");
        }
    };

    componentDidMount(): void {
        const item = this.props.item;
        this.setAttachmentMarkToLastVisibleCol(item.hasAttachments || item.attachments.length > 0, item.isLockedToOtherUser(this.props.userCode));
    }

    componentDidUpdate(prevProps: ITaskDataRowListLineProp) {
        const item = this.props.item;
        if (Base.isNullOrUndefined(prevProps.item) || Base.isNullOrUndefined(item)) return;
        const hasAttachment = item.hasAttachments || item.attachments.length > 0;
        const isLockedToOtherUser = item.isLockedToOtherUser(this.props.userCode);
        if (item.id !== prevProps.item.id || hasAttachment !== this.hasAttachment || isLockedToOtherUser !== this.isLockedToOtherUser) {
            this.setAttachmentMarkToLastVisibleCol(hasAttachment, isLockedToOtherUser);
        }
    }

    render() {
        const props = this.props;
        const item = props.item;
        const lineTaskTypeCols = props.lineTaskTypeCols;
        return (
            <ListLine
                id={item.id}
                classes={"line" + (props.isTotalLine ? " total" : "")}
                first={false}
                selected={props.selectedId === item.id}
                onClick={props.onClick}
                onLongPress={props.onLongPress}
                onRefreshList={null}>
                <div className="row" ref={(elem) => { this.rowDiv = elem; }}>
                    {lineTaskTypeCols.map((lineTaskTypeCol) =>
                        <TaskDataRowListLineCol
                            key={lineTaskTypeCol.field}
                            field={lineTaskTypeCol.field}
                            taskType={props.taskType}
                            dataRow={item}
                            lineTaskTypeCol={lineTaskTypeCol}
                            modifyUserName={item.modifyUserName}
                        />
                    )}
                </div>
            </ListLine>
        );
    }
}

//TaskDataRowList
//***************************************************************
export interface ITaskDataRowListProp {
    isReadOnly: boolean;
    userIsIsSystemAdmin: boolean;
    isNewTask: boolean;
    userCode: string;
    cameraIds: string[];
    taskId: string;
    companyId: string;
    taskType: ITaskEditTypeItem;
    taskDataRows: ITaskDataRow[];
    photoAttachmentIds: string[];
    newCameraFileAttachment: FileAttachment;
    onAddPhoto: () => void;
    //onRemove: (id: string) => void;
    onUpdate: (oldTaskDataRowId: string, taskDataRow: ITaskDataRow) => void;
    onUpdateTaskDataRows: (taskDataRows: ITaskDataRow[]) => void;
}

interface ITaskDataRowListState {
    page: number;
    hasMore: boolean;
    totalItemCount: number;
    isLoading: boolean;
    selectedId: string;
    showListMenu: boolean;
    showEditDialog: boolean;
    editDialogItem: ITaskDataRow;
    editDialogItemIsNew: boolean;
    totalRowTaskType: ITaskEditTypeItem;
    totalRow: ITaskDataRow;
}

export class TaskDataRowList extends React.Component<ITaskDataRowListProp, ITaskDataRowListState> {
    constructor(props) {
        super(props);
        this.state = { page: 1, hasMore: true, totalItemCount: 0, isLoading: false, selectedId: null, showListMenu: false, showEditDialog: false, editDialogItem: null, editDialogItemIsNew: false, totalRowTaskType: null, totalRow: null };
    }

    setLoading = (value: boolean) => {
        this.setState({
            isLoading: value
        });
    };

    searchItems = async (page: number, resetItems: boolean, refreshList: boolean) => {
        const props = this.props;
        const state = this.state;
        if (props.isNewTask) {
            return;
        }
        this.setLoading(true);
        const result = await AppUtils.callService(() => taskService.getTaskDataRowList(props.taskId, !refreshList ? infiniteListPageHalfSize : (page * infiniteListPageHalfSize), !refreshList ? page : 1), false, false, () => this.setLoading(false));
        if (!result) return;
        const listItems = Base.getListItems<TaskDataRow>(props.taskDataRows, state.selectedId, [], result.items, resetItems, refreshList);
        //Calculate total row
        let totalRowTaskType = state.totalRowTaskType;
        if (totalRowTaskType === null || totalRowTaskType.id !== props.taskType.id) {
            totalRowTaskType = TaskEditTypeItem.getTotalRowTaskEditTypeItem(props.taskType);
        }
        let totalRow = state.totalRow;
        if (totalRowTaskType.lineCols.length > 0) {
            totalRow = TaskEditTypeItem.getTotalRow(totalRowTaskType, listItems.items);
        }
        this.setState({
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            totalItemCount: result.totalItemCount,
            totalRowTaskType,
            totalRow
        });
        props.onUpdateTaskDataRows(listItems.items);
    };

    refreshList = () => {
        this.searchItems(this.state.page, false, true);
    };

    handleLoadMore = () => {
        const state = this.state;
        if (!state.hasMore) return;
        this.searchItems(state.page + 1, false, false);
    };

    componentDidMount(): void {
        const state = this.state;
        this.searchItems(state.page, false, false);
    }

    //#region Add/Edit
    handleAddClick = async () => {
        const result = await AppUtils.callService(() => taskService.getTaskDataRowDefaultValues(this.props.taskId), false);
        if (!result) return;
        const taskDataRow = new TaskDataRow();
        taskDataRow.id = Base.getGuid();
        taskDataRow.initializeToTaskType(this.props.taskType, result.cols);
        this.setState({
            showEditDialog: true,
            editDialogItem: taskDataRow,
            editDialogItemIsNew: true
        });
    };

    handleEdit = async (id: string) => {
        if (this.state.isLoading) return;
        const props = this.props;
        this.setLoading(true);
        const result = await AppUtils.callService(() => taskService.getTaskDataRowEdit(this.props.taskId, id), false, false, () => this.setLoading(false));
        if (!result) return;
        result.item.initializeCols(props.taskType);
        this.setState({
            showEditDialog: true,
            editDialogItem: result.item,
            editDialogItemIsNew: false
        });
    };

    private getSaveData = async (cols: ITaskDataRowCol[], attachments: IFileAttachment[], photoAttachmentIds: string[], removedAttachmentIds: string[]): Promise<SaveDataObj> => {
        const props = this.props;
        const state = this.state;
        const data = new SaveDataObj();
        data.append("taskId", props.taskId);
        data.append("companyId", props.companyId);
        //TaskDataRows
        const saveTaskDataRow = SaveTaskDataRow.createSaveTaskDataRow(props.taskType.formCols, state.editDialogItem, cols);
        saveTaskDataRow.id = !state.editDialogItemIsNew ? state.editDialogItem.id : Base.getGuid();
        data.append("taskDataRow", saveTaskDataRow);
        //Attachments
        const newAttachments: SaveAttachmentBase64[] = [];
        const newAttachmentsIds: string[] = [];
        for (const fileAttachment of attachments.filter(i => !!i.file && i.isNew())) {
            const attachment = await SaveAttachmentBase64.fromFileAttachment(photoAttachmentIds.indexOf(fileAttachment.id) > -1 ? "photo" : "", fileAttachment);
            if (!attachment) continue;
            newAttachments.push(attachment);
            newAttachmentsIds.push(fileAttachment.id);
        }
        data.append("attachments", newAttachments);
        data.append("newAttachmentsIds", newAttachmentsIds);
        data.append("removeAttachmentIds", removedAttachmentIds);
        return data;
    };
    
    unlockTaskDataRow = async (id: string, lockToCurrentUser: boolean, afterUnlock: (result: IUnlockResult) => void) => {
        const state = this.state;
        const result = await AppUtils.callService(() => taskService.unlockTaskDataRow(this.props.taskId, id, lockToCurrentUser));
        if (!result) return;
        if (state.editDialogItem) {
            state.editDialogItem.lockedUserCode = result.lockedUserCode;
            state.editDialogItem.lockedDate = result.lockedDate;
        }
        if (!Base.isNullOrUndefined(afterUnlock)) {
            afterUnlock(result);
        }
    };

    checkErrors = (cols: ITaskDataRowCol[]): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            TaskTypeCol.validateInputOfRequiredTaskTypeCols(this.props.taskType.formCols, cols, null,
                (typeCol: ITaskTypeCol) => {
                    AppUtils.showErrorMessage(String.format(Translations.FieldParameterValueMustBeDefined, typeCol.title));
                    return resolve(false);
                });
            return resolve(true);
        });
    };

    handleTaskDataRowDialogOk = async (cols: ITaskDataRowCol[], attachments: IFileAttachment[], photoAttachmentIds: string[], removedAttachmentIds: string[]) => {
        const state = this.state;
        const props = this.props;
        if (!await AppUtils.validate(() => this.checkErrors(cols))) return;
        const taskDataRow = new TaskDataRow(state.editDialogItem);
        const isLockedToOtherUser = taskDataRow.isLockedToOtherUser(props.userCode);
        if (isLockedToOtherUser) {
            AppUtils.showErrorMessage(Translations.LockedTaskDataRowCannotBeSaved);
            return;
        }
        const saveData = await this.getSaveData(cols, attachments, photoAttachmentIds, removedAttachmentIds);
        const result = await AppUtils.callService(() => taskService.saveTaskDataRow(saveData.obj));
        if (!result) return;
        if (state.editDialogItemIsNew) {
            taskDataRow.id = result.id;
        }
        for (const attachment of attachments) {
            for (const savedAttachmentId of result.savedAttachmentsIds) {
                if (attachment.id === savedAttachmentId.oldId) {
                    attachment.id = savedAttachmentId.newId;
                    break;
                }
            }
        }
        taskDataRow.attachments = attachments.slice(0);
        taskDataRow.cols = cols.slice(0);
        const unlockOnClose = taskDataRow.isLockedToUser(props.userCode) && !taskDataRow.isNew();
        if (unlockOnClose) {
            await this.unlockTaskDataRow(taskDataRow.id, false, (unlockResult) => {
                taskDataRow.lockedUserCode = unlockResult.lockedUserCode;
                taskDataRow.lockedDate = unlockResult.lockedDate;
            });
        }
        this.refreshList();
        this.setState({
            showEditDialog: false,
            editDialogItem: null
        });
    };

    handleTaskDataRowDialogCancel = () => {
        const state = this.state;
        const props = this.props;
        const taskType = props.taskType;
        const taskDataRow = new TaskDataRow(state.editDialogItem);
        for (let i = taskDataRow.cols.length - 1; i >= 0; i --) {
            const dataCol = taskDataRow.cols[i];
            const lineCol = taskType.lineCols.find(j => j.field === dataCol.field);
            if (lineCol) continue;
            taskDataRow.cols.splice(i, 1);
        }
        const unlockOnClose = taskDataRow.isLockedToUser(props.userCode) && !taskDataRow.isNew();
        if (unlockOnClose) {
            this.unlockTaskDataRow(taskDataRow.id, false, (unlockResult) => {
                taskDataRow.lockedUserCode = unlockResult.lockedUserCode;
                taskDataRow.lockedDate = unlockResult.lockedDate;
                props.onUpdate(taskDataRow.id, taskDataRow);
            });
        } else if (!taskDataRow.isNew()) {
            props.onUpdate(taskDataRow.id, taskDataRow);
        }
        this.setState({
            showEditDialog: false,
            editDialogItem: null
        });
    };
    //#endregion
    
    handleLineClick = (id: string) => {
        this.setState({
            selectedId: id
        });
        this.handleEdit(id);
    };

    handleLineLongPress = (id: string) => {
        this.setState({
            selectedId: id,
            showListMenu: true
        });
    };

    handleMenuRemove = async () => {
        const props = this.props;
        const selectedId = this.state.selectedId;
        if (!selectedId) return;
        const result = await AppUtils.showConfirmationDialog(Translations.DoYouReallyWantToRemoveTaskDataRow);
        if (result !== ConfirmationDialogResult.Yes) return;
        const success = await AppUtils.callService(() => taskService.removeTaskDataRow(props.taskId, selectedId));
        if (!success) return null;
        this.refreshList();
        this.setState({
            showListMenu: false,
            showEditDialog: false
        });
    };

    handleMenuCancel = () => {
        this.setState({
            showListMenu: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const taskType = props.taskType;
        const taskDataRows = props.taskDataRows;
        return (
            <div>
                <div className="fullLeft">
                    <label className="control-label">{Translations.TaskDataRows + " (" + taskDataRows.length + (state.totalItemCount > taskDataRows.length ? "/" + state.totalItemCount : "") + ")"}</label>
                    {!props.isReadOnly && !props.isNewTask &&
                        <ToolButton
                            tooltip={Translations.Add}
                            classes={"round right mono add"}
                            onClick={this.handleAddClick}/>
                    }
                </div>
                <div className="jsonFormLineData">
                    <TaskDataRowListTitleRow
                        taskTypeCols={taskType.lineCols}
                    />
                    {props.isNewTask &&
                        <div className="fullLeft">
                            <div className="alert alert-info" role="alert">{Translations.TaskMustBeSavedBeforeNewDataRowsCanBeAdded}</div>
                        </div>
                    }
                    {taskDataRows.length > 0 &&
                        <div className="fullLeft lineContainer taskDataRows">
                            {taskDataRows.map((taskDataRow) =>
                                <TaskDataRowListLine
                                    key={taskDataRow.id}
                                    userCode={props.userCode}
                                    isReadOnly={props.isReadOnly}
                                    selectedId={state.selectedId}
                                    taskType={taskType}
                                    lineTaskTypeCols={taskType.lineCols}
                                    item={taskDataRow}
                                    onClick={this.handleLineClick}
                                    onLongPress={!props.isReadOnly ? this.handleLineLongPress : null}
                                />
                            )}
                            {state.totalRowTaskType !== null && state.totalRow !== null &&
                                <TaskDataRowListLine
                                    userCode={props.userCode}
                                    isReadOnly={true}
                                    selectedId={state.selectedId}
                                    taskType={state.totalRowTaskType}
                                    lineTaskTypeCols={state.totalRowTaskType.lineCols}
                                    item={state.totalRow}
                                    isTotalLine={true}
                                    onClick={() => {}}
                                    onLongPress={null}
                                />
                            }
                            {state.hasMore &&
                                <div className="loadMoreLink" onClick={this.handleLoadMore}>{Translations.LoadMore}</div>
                            }
                        </div>
                    }
                </div>
                {state.isLoading &&
                    <img className="loadingLocal" alt={Translations.Loading} src={Loader} />
                }
                {this.state.showListMenu &&
                    <TaskDataRowListMenu
                        onRemove={this.handleMenuRemove}
                        onCancel={this.handleMenuCancel}
                    />
                }
                {this.state.showEditDialog &&
                    <TaskDataRowDialog
                        isReadOnly={props.isReadOnly || state.editDialogItem.isLockedToOtherUser(props.userCode)}
                        userIsIsSystemAdmin={props.userIsIsSystemAdmin}
                        userCode={props.userCode}
                        cameraIds={props.cameraIds}
                        adding={state.editDialogItemIsNew}
                        taskType={taskType}
                        item={state.editDialogItem}
                        photoAttachmentIds={props.photoAttachmentIds}
                        newCameraFileAttachment={props.newCameraFileAttachment}
                        unlockTaskDataRow={this.unlockTaskDataRow}
                        onAddPhoto={this.props.onAddPhoto}
                        onOk={this.handleTaskDataRowDialogOk}
                        onRemove={this.handleMenuRemove}
                        onCancel={this.handleTaskDataRowDialogCancel}
                    />
                }
            </div>
        );
    }
}